<template>
  <div>
    <Spinner v-if="loading" />

    <div v-if="editing" class="order-item-toolbar row mb-1">
      <div class="col-12">
        <span class="font-weight-bold mr-3" v-if="optionAddServiceAvailable || optionDiscountAvailable || optionCustomerApprovalAvailable || optionSupplierDetailsAvailable || optionOrderEditorAvailable">Tools</span>
        <FormButton ref="applyAllServiceButton" class="btn-default btn-xs mr-2" @click="displayApplyAllService" v-if="optionAddServiceAvailable">
          <i class="far fa-plus"></i> Apply Service
        </FormButton>
        <FormButton ref="applyAllDiscountButton" class="btn-default btn-xs mr-2" @click="displayApplyAllDiscount" v-if="optionDiscountAvailable">
          <i class="far fa-percent order-item-icon-discount"></i> Discount
        </FormButton>
        <FormButton ref="applyAllCustomerApprovalButton" class="btn-default btn-xs mr-2" @click="displayApplyAllCustomerApproval" v-if="optionCustomerApprovalAvailable">
          <i class="far fa-user-circle order-item-icon-customer-approval"></i> Customer Approval
        </FormButton>
        <FormButton ref="applyAllSupplierButton" class="btn-default btn-xs mr-2" @click="displayApplyAllSupplier" v-if="optionSupplierDetailsAvailable">
          <i class="far fa-industry order-item-icon-supplier"></i> Supplier
        </FormButton>
        <FormButton ref="orderEditorButton" class="btn-default btn-xs mr-2" @click="displayOrderEditor" v-if="optionOrderEditorAvailable">
          <i class="fad fa-shapes order-item-icon-editor"></i> Order Editor
        </FormButton>
      </div>

      <OrderItemApplyAllServicePopover ref="applyAllServicePopover" :itemList="filterEditableOrderItemList(itemList)" :stockLookupList="stockLookupList" @addService="addAllService" />
      <OrderItemApplyAllDiscountPopover ref="applyAllDiscountPopover" :itemList="filterEditableOrderItemList(itemList)" @applyDiscount="applyAllDiscount" />
      <OrderItemApplyAllCustomerApprovalPopover ref="applyAllCustomerApprovalPopover" :itemList="filterEditableOrderItemList(itemList)" @applyApproval="applyAllCustomerApproval" />
      <OrderItemApplyAllSupplierPopover ref="applyAllSupplierPopover" :itemList="filterEditableOrderItemList(itemList)" :supplierLookupList="supplierLookupList" @applySupplier="applyAllSupplier" />
    </div>

    <StaticDataTable class="static-table-list order-item-list" v-if="!loading" :striped="false" :hover="editing || showTotals">
      <template v-slot:header>
        <tr v-if="editing">
          <th width="9%">Type</th>
          <th width="8%">Stock</th>
          <th width="11%">Description</th>
          <th width="9%" v-if="isPriceGroupsAvailable">Pricing</th>
          <th width="4%">Qty</th>
          <th width="5%">Height</th>
          <th width="5%">Width</th>
          <th width="8%" v-if="isMarkAsAvailable">Mark As</th>
          <th width="6%" v-if="showUnits">Units / Panel</th>
          <th width="5%" v-if="showUnits">Total Units</th>
          <th width="5%" v-if="isUnitPriceAvailable">Price / Unit</th>
          <th width="5%" v-if="isLinePriceAvailable">Line Price</th>
          <th width="13%" v-if="isSupplierDetailsAvailable">Supplier Setup</th>
          <th width="7%" v-if="isActionsAvailable">Actions</th>
        </tr>
        <tr v-if="!editing">
          <th>Type</th>
          <th>Stock</th>
          <th>Description</th>
          <th v-if="isPriceGroupsAvailable">Pricing</th>
          <th>Qty</th>
          <th>Height</th>
          <th>Width</th>
          <th v-if="isMarkAsAvailable">Mark As</th>
          <th v-if="showUnits">Units / Panel</th>
          <th v-if="showUnits">Total Units</th>
          <th v-if="isUnitPriceAvailable">Price / Unit</th>
          <th v-if="isLinePriceAvailable">Line Price</th>
          <th v-if="isSupplierDetailsAvailable">Supplier Setup</th>
          <th v-if="isActionsAvailable">Actions</th>
        </tr>
      </template>
      <template v-slot:body>
        <template v-for="(item, index) in itemList">

          <!-- Sub Header between items. Helps keep track of columns for some people -->
          <tr v-if="index > 0 && item.isParentItem" class="order-item-sub-header" :key="`${item.id}-heading`">
            <td>Type</td>
            <td>Stock</td>
            <td>Description</td>
            <td v-if="isPriceGroupsAvailable">Pricing</td>
            <td>Qty</td>
            <td>Height</td>
            <td>Width</td>
            <td v-if="isMarkAsAvailable">Mark As</td>
            <td v-if="showUnits">Units / Panel</td>
            <td v-if="showUnits">Total Units</td>
            <td v-if="isUnitPriceAvailable">Price / Unit</td>
            <td v-if="isLinePriceAvailable">Line Price</td>
            <td v-if="isSupplierDetailsAvailable">Supplier Setup</td>
            <td v-if="isActionsAvailable">Actions</td>
          </tr>

          <OrderItemListItem            
            :key="item.id"
            :rootItemIndex="getRootItemIndex(item)"
            :ref="`order-item-${item.id}`"
            :editing="editing"
            :isNewOrder="isNewOrder"
            :orderItem="item"
            :orderType="orderType"
            :parentItem="itemParent(item)"
            :rootItem="findItemRootForItem(item)"
            :itemList="cachedItemList"
            :stockLookupList="stockLookupList"
            :stockPriceGroupList="stockPriceGroupList"
            :supplierLookupList="supplierLookupList"
            :contactId="contactId"
            :newContactSalesRateId="newContactSalesRateId"
            :priceType="priceType"
            :showPricing="showPricing"
            :showUnits="showUnits"
            :showActions="isActionsAvailable"
            :showMarkAs="showMarkAs"
            :showPriceGroups="showPriceGroups"

            :optionDiscountAvailable="optionDiscountAvailable"
            :optionCustomerApprovalAvailable="optionCustomerApprovalAvailable"
            :optionExternalAvailable="optionExternalAvailable"
            :optionEdgeworkAvailable="optionEdgeworkAvailable"
            :optionNewItemGlassAvailable="optionNewItemGlassAvailable"
            :optionAddServiceAvailable="optionAddServiceAvailable"
            :optionAddGeneralAvailable="optionAddGeneralAvailable"
            :optionBlockRemovingExternal="optionBlockRemovingExternal"
            :optionSupplierDetailsAvailable="optionSupplierDetailsAvailable"
            :optionCancelItemAvailable="optionCancelItemAvailable"
            :optionPreviewItemAvailable="optionPreviewItemAvailable"

            @childItemAdd="addItemClicked($event, index)"
            @itemRemove="removeItem(item.id)"
            @itemCancel="$emit('itemCancel', item.id)"
            @copyGroup="copyGroupToNewGroup(item.id)"
            @copySupplierToGroup="copySupplierToGroup(item.id, $event)"
            @selectGlassClick="itemSelectGlass(index)"
            @selectNCRClick="itemSelectNCR(index)"
            @update="itemUpdatedForId(item.id, $event)"
            @pricingConfigUpdate="itemPricingConfigUpdated(index, $event)"
            @templateUpdate="itemTemplateUpdated(index, $event)"
            @itemLoadingUpdate="itemLoadingUpdate(index, $event)"
            @toggleExternal="itemToggleExternal(index)"
            @toggleNCRIsRepair="itemToggleNCRRepair(index)"
            />


          <!--
            SVG Display
            Only available if OrderEditor feature available to the account
          -->
          <tr :class="[`order-item-row-group-${item.groupStockType}`, 'order-item-row-preview']" v-if="item.stockType == InventoryStockTypes.glass && item.svgDisplayData && !editing && item.showingGlass && accountHasOrderEditor" :key="`${item.id}-preview`">
            <td :colspan="13 + (isActionsAvailable ? 1 : 0)">
              <div v-html="item.svgDisplayData" />
            </td>
          </tr>

        </template>
        <tr v-if="editing && itemList.length > 0 && (isNewItemAvailable || showTotals)" class="order-item-spacer">
          <td :colspan="13 + (isActionsAvailable ? 1 : 0)"> </td>
        </tr>
        <tr class="order-item-row order-item-row-group-newitem" v-if="isNewItemAvailable">
          <td colspan="3">
            <FormSearchableSelect
              v-if="editing"
              ref="newItemEntry"
              :options="newItemLookup"
              @input="newItemSelected"
              placeholder="New Item" />
          </td>
          <td :colspan="9 + (isActionsAvailable ? 1 : 0)"> </td>
        </tr>

        <template v-if="!editing && showTotals && showPricing">
          <tr class="static-datatable-row-nostripe border-top table-totals-row">
            <td :colspan="3 + (isPriceGroupsAvailable ? 1 : 0)">
              <span v-if="showUnits && showGlassTotals" class="font-weight-bold">Glass Totals</span>
            </td>
            <td colspan="1" class="text-center font-weight-bold">
              <span v-if="showUnits && showGlassTotals && calculateTotalQuantityGlass() > 0">{{calculateTotalQuantityGlass()}}</span>
            </td>
            <td :colspan="3 + (isMarkAsAvailable ? 1 : 0)"> </td>
            <td colspan="1">
              <span class="order-item-non-editable font-weight-bold" v-if="showUnits && showGlassTotals && calculateTotalUnitsGlass() > 0">{{calculateTotalUnitsGlass() | formatDecimal(3)}} m&sup2;</span>
            </td>
            <td class="text-right">Sub Total:</td>
            <td :colspan="1 + (isActionsAvailable ? 1 : 0)">{{calculateSubTotalPrice() | formatCurrency}}</td>
          </tr>
          <tr class="static-datatable-row-nostripe table-totals-row table-totals-row-discount-total" v-if="calculateDiscountTotalPrice() > 0">
            <td :colspan="8 + (isPriceGroupsAvailable ? 1 : 0) + (isMarkAsAvailable ? 1 : 0)"> </td>
            <td class="text-right">Discounts:</td>
            <td :colspan="1 + (isActionsAvailable ? 1 : 0)">{{-calculateDiscountTotalPrice() | formatCurrency}}</td>
          </tr>
          <tr class="static-datatable-row-nostripe table-totals-row">
            <td :colspan="2" v-if="isSupplierPricing">
              <strong>Total Weight:</strong> {{formatDecimal(calculateTotalWeight(), 0, true)}}kg
            </td>
            <td :colspan="6 + (isPriceGroupsAvailable ? 1 : 0) + (isMarkAsAvailable ? 1 : 0) + (isSupplierPricing ? 0 : 2)"> </td>
            <td class="text-right">{{totalsTaxDisplayName}}:</td>
            <td :colspan="1 + (isActionsAvailable ? 1 : 0)">{{calculateTaxPrice() | formatCurrency}}</td>
          </tr>
          <tr class="static-datatable-row-nostripe table-totals-row table-totals-row-grand-total">
            <td :colspan="8 + (isPriceGroupsAvailable ? 1 : 0) + (isMarkAsAvailable ? 1 : 0)"> </td>
            <td class="text-right">Total:</td>
            <td :colspan="1 + (isActionsAvailable ? 1 : 0)">{{calculateTotalPrice() | formatCurrency}}</td>
          </tr>
        </template>
        <template v-if="editing && showTotals && showPricing">
          <tr class="static-datatable-row-nostripe" v-if="showTotals">
            <td :colspan="3 + (isPriceGroupsAvailable ? 1 : 0)">
              <span v-if="showUnits" class="font-weight-bold">Glass Totals</span>
            </td>
            <td colspan="1" class="text-center font-weight-bold">
              <span v-if="showUnits && calculateTotalQuantityGlass() > 0">{{calculateTotalQuantityGlass()}}</span>
            </td>
            <td :colspan="3 + (isMarkAsAvailable ? 1 : 0)"> </td>
            <td colspan="1">
              <span class="order-item-non-editable font-weight-bold" v-if="showUnits && calculateTotalUnitsGlass() > 0">{{calculateTotalUnitsGlass() | formatDecimal(3)}} m&sup2;</span>
            </td>
            <td colspan="1" class="text-right">Sub Total:</td>
            <td :colspan="1 + (isActionsAvailable ? 1 : 0)">{{calculateSubTotalPrice() | formatCurrency}}</td>
          </tr>
          <tr class="static-datatable-row-nostripe table-totals-row table-totals-row-discount-total" v-if="calculateDiscountTotalPrice() > 0">
            <td :colspan="7 + (isPriceGroupsAvailable ? 1 : 0) + (isMarkAsAvailable ? 1 : 0)"> </td>
            <td colspan="2" class="text-right">Discounts:</td>
            <td :colspan="1 + (isActionsAvailable ? 1 : 0)">{{-calculateDiscountTotalPrice() | formatCurrency}}</td>
          </tr>
          <tr class="static-datatable-row-nostripe table-totals-row">
            <td :colspan="2" v-if="isSupplierPricing">
              <strong>Total Weight:</strong> {{formatDecimal(calculateTotalWeight(), 0, true)}}kg
            </td>
            <td :colspan="5 + (isPriceGroupsAvailable ? 1 : 0) + (isMarkAsAvailable ? 1 : 0) + (isSupplierPricing ? 0 : 2)"> </td>
            <td colspan="2" class="text-right">{{totalsTaxDisplayName}}:</td>
            <td :colspan="1 + (isActionsAvailable ? 1 : 0)">{{calculateTaxPrice() | formatCurrency}}</td>
          </tr>
          <tr class="static-datatable-row-nostripe table-totals-row table-totals-row-grand-total">
            <td :colspan="7 + (isPriceGroupsAvailable ? 1 : 0) + (isMarkAsAvailable ? 1 : 0)"> </td>
            <td colspan="2" class="text-right">Total:</td>
            <td :colspan="1 + (isActionsAvailable ? 1 : 0)">{{calculateTotalPrice() | formatCurrency}}</td>
          </tr>
        </template>
      </template>
    </StaticDataTable>    
  </div>
</template>

<style lang="scss">


</style>

<script>
import _ from 'lodash'
import { v4 as uuidv4 } from 'uuid'
import { mapState } from "vuex"
import currency from 'currency.js'
import orderItemMixin from '@/modules/orders/mixins/orderItemMixin'

import Utils from '@/utils/utils'
import FilterUtils from '@/utils/filterUtils'
import StaticDataTable from '@/components/table/staticDataTable.vue'
import OrderItemListItem from '@/modules/orders/components/orderItemListItem'
import OrderSelectGlassLevelDialog from '@/modules/orders/views/orderSelectGlassLevelDialog'
import OrderSelectGlassSizeDialog from '@/modules/orders/views/orderSelectGlassSizeDialog'
import OrderItemNCRDialog from '@/modules/orders/views/orderItemNCRDialog'

import OrderItemApplyAllServicePopover from '@/modules/orders/components/orderItemApplyAllServicePopover'
import OrderItemApplyAllDiscountPopover from '@/modules/orders/components/orderItemApplyAllDiscountPopover'
import OrderItemApplyAllCustomerApprovalPopover from '@/modules/orders/components/orderItemApplyAllCustomerApprovalPopover'
import OrderItemApplyAllSupplierPopover from '@/modules/orders/components/orderItemApplyAllSupplierPopover'

import { InventoryStockTypes, InventoryStockTypeLookup, UOMTypes, UOMTypeUnitsLookup, AccountSettingKeys, DeliveryPricingTypes, OrderItemEdgeworkTypes, OrderTypes, PriceTypes, OrderItemEdgeworkTypeLookup, FilterOptionTypes } from '@/store/types'

export default {
  name: 'OrderItemList',
  mixins: [orderItemMixin],
  components: {
    StaticDataTable,
    OrderItemListItem,
    OrderItemApplyAllServicePopover,
    OrderItemApplyAllDiscountPopover,
    OrderItemApplyAllCustomerApprovalPopover,
    OrderItemApplyAllSupplierPopover
  },
  model: {
    prop: 'value',
    event: 'update'
  },
  props: {
    editing: Boolean,
    orderType: {
      type: String,
      default: OrderTypes.order
    },
    showGlassTotals: {
      type: Boolean,
      default: true
    },
    showTotals: {
      type: Boolean,
      default: true
    },
    showPricing: {
      type: Boolean,
      default: true
    },
    showUnits: {
      type: Boolean,
      default: true
    },
    showPriceGroups: {
      type: Boolean,
      default: true
    },
    showMarkAs: {
      type: Boolean,
      default: true
    },
    value: {
      type: Array,
      default: () => ([])
    },
    contactId: [Number, String],
    taxRateId: Number,
    newContactSalesRateId: Number,     // New contacts have a selectable sales rate
    isNewOrder: Boolean,
    customerData: Object,
    isDespatchDelivery: Boolean,
    deliveryStockId: Number,
    priceType: {
      type: String,
      default: PriceTypes.customer
    },
    supplierLookupList: Array,

    // Options to toggle available tools
    optionDiscountAvailable: {
      type: Boolean,
      default: true
    },
    optionCustomerApprovalAvailable: {
      type: Boolean,
      default: true
    },
    optionEdgeworkAvailable: {
      type: Boolean,
      default: true
    },
    optionExternalAvailable: {
      type: Boolean,
      default: true
    },
    optionAddServiceAvailable: {
      type: Boolean,
      default: true
    },
    optionAddGeneralAvailable: {
      type: Boolean,
      default: true
    },
    optionBlockRemovingExternal: {
      type: Boolean,
      default: false
    },
    optionSupplierDetailsAvailable: {
      type: Boolean,
      default: false
    },
    optionNewItemGlassAvailable: {
      type: Boolean,
      default: true
    },
    optionNewItemGeneralAvailable: {
      type: Boolean,
      default: true
    },
    optionNewItemTemplateAvailable: {
      type: Boolean,
      default: true
    },
    optionCancelItemAvailable: {
      type: Boolean,
      default: false
    },
    optionOrderEditorAvailable: {
      type: Boolean,
      default: false
    },
    optionPreviewItemAvailable: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      loading: false,
      overrideWarning: false,
      containerList: [],
      stockList: [],
      stockLookupList: [],
      taxRateList: [],
      stockPriceGroupList: [],
      orderStampConfig: {},

      itemList: this.$props.value,

      itemLoadingStatusInfo: {},

      // We stored a cached version for item lookups that only gets updated for major changes
      // This is a performance update to prevent OrderItemListItem component re-renders for every single update
      // This cached versioned needs to be updated when adding/removing items
      cachedItemList: _.cloneDeep(this.$props.value),

      settings: {
        deliveryStockId: this.$props.deliveryStockId,
        deliveryStock: null,
        minPriceStockId: null,
        minPriceStock: null,
        minInvoiceStockId: null,
        minInvoiceStock: null,
        templateHandlingStockId: null
      }
    }
  },
  computed: {
    isNCR() {
      return this.orderType == OrderTypes.nonChargeableRework
    },
    isNewItemAvailable() {
      return this.editing && !this.isNCR && (this.$props.optionNewItemGlassAvailable || this.$props.optionNewItemGeneralAvailable || this.$props.optionNewItemTemplateAvailable)
    },
    isCustomerPricing() {
      return this.priceType == PriceTypes.customer
    },
    isSupplierPricing() {
      return this.priceType == PriceTypes.supplier
    },
    isPriceGroupsAvailable() {
      return this.showPriceGroups
    },
    isMarkAsAvailable() {
      return !this.$props.optionSupplierDetailsAvailable && this.showMarkAs
    },
    isUnitPriceAvailable() {
      return this.showPricing
    },
    isLinePriceAvailable() {
      return this.showPricing
    },
    isSupplierDetailsAvailable() {
      return this.$props.optionSupplierDetailsAvailable
    },
    isActionsAvailable() {
      if(this.editing || this.isNCR || this.optionCancelItemAvailable || this.optionPreviewItemAvailable)
        return true
      return false
    },
    newItemLookup() {
      var itemLookup = []
      if(this.$props.optionNewItemGlassAvailable)
        itemLookup.push({ name: 'Glass', value: InventoryStockTypes.glass })
      if(this.$props.optionNewItemGeneralAvailable)
        itemLookup.push({ name: 'General', value: InventoryStockTypes.general })
      if(this.$props.optionNewItemTemplateAvailable)
        itemLookup.push({ name: 'Template', value: InventoryStockTypes.template })

      return itemLookup
    },
    accountHasOrderEditor() {
      return this.accountHasProductFeature(this.AccountProductFeatureTypes.orderEditor)
    },
    totalsTaxDisplayName() {
      // Based on items
      var taxRateIdList = _.uniq(_.compact(this.itemList.map(item => item.taxRateId)))
      if(taxRateIdList.length == 0) {
        // If no items, use set tax rate id
        let taxRate = this.taxRateList.find(taxRate => taxRate.id == this.taxRateId)
        if(taxRate)
          return taxRate.displayName

        // Fall back to company tax rate
        return this.$store.state.account.salesTaxRate.displayName
      }

      if(taxRateIdList.length == 1) {
        let taxRate = this.taxRateList.find(taxRate => taxRate.id == taxRateIdList[0])
        if(taxRate)
          return taxRate.displayName
        return 'Tax'
      }

      return 'Multiple Tax Rates'    
    },
    taxRatePercent() {
      var taxRate = this.taxRateList.find(taxRate => taxRate.id == this.taxRateId)
      if(taxRate)
        return (taxRate.rate ? taxRate.rate : 0)

      return (this.$store.state.account.salesTaxRate.rate ? this.$store.state.account.salesTaxRate.rate : 0)
    }
  },
  watch: {
    value: function(newVal, oldVal) {
      this.$data.itemList = _.cloneDeep(newVal)
      this.$data.cachedItemList = _.cloneDeep(this.$data.itemList)

      // If no delivery stock set, attempt to find it in the item list
      // Used for when loading an existing item list
      if(this.isDespatchDelivery) {
        if(!this.settings.deliveryStockId && !this.settings.deliveryStock) {
          this.findDeliveryStock()
        } else {
          // If the delivery item was changed, update internal settings
          var deliveryItem = this.itemList.find(item => item.isDeliveryItem)
          if(deliveryItem && this.settings.deliveryStock) {
            if(deliveryItem.stockId != this.settings.deliveryStock.id) {
              this.settings.deliveryStock = null
              this.settings.deliveryStockId = deliveryItem.stockId
              this.requestDeliveryStockData()
            }
          }
        }
      }
    },
    isDespatchDelivery: function(newVal) {
      this.itemListUpdated()
    },
    contactId: function(newVal) {
      this.itemListUpdated()
    },
    taxRateId: function(newVal) {
      // Update tax percentages
      this.updateItemTaxRates()
    },
    customerData: function(newVal) {
      this.itemListUpdated()
    },
    editing: function(newVal, oldVal) {
      // If now editing: load the data, then apply some lookups to each item
      if(newVal) {
        this.loading = true
        this.overrideWarning = false
        this.overrideUpdated(this.overrideWarning)

        this.requestData({ ignoreLoadingState: true })
        .then(async () => {
          // TODO: Check what we need to do here when loading an item list
          this.loading = false
        })
      }
    },
    deliveryStockId: function(newVal) {
      this.settings.deliveryStockId = this.$props.deliveryStockId
      this.requestDeliveryStockData()
    }
  },
  created() {
    this.InventoryStockTypes = InventoryStockTypes
    this.InventoryStockTypeLookup = InventoryStockTypeLookup
  },
  mounted() {
    // Note: Calling page of the entry list will call the requestData() method
    // It needs to know when loaded to perform other actions

    // Request base level data for editing / non-editing modes
    this.requestBaseData()
  },
  methods: {
    requestBaseData() {
      Promise.all([
        this.$apiService.adminService.getTaxRateList()
      ])
      .then(results => {
        this.$data.taxRateList = results[0].data
      })
      .catch(err => {
        this.displayNetworkError(err, 'Error', 'Error loading data')
      })
    },

    // Request associated data with the lookups for editing mode
    // Options are:
    //   ignoreLoadingState: Ignores the loading state, useful when loading an existing list to stop UI popping effects while loading/setting values
    requestData(options = {}) {
      return new Promise((resolve, reject) => {
        if(!options.ignoreLoadingState)
          this.loading = true

        Promise.all([
          this.$apiService.inventoryService.getStockList({ include: 'stockSalesPrice' }),
          this.$apiService.pricingService.getStockPriceGroupList(),
          this.$apiService.adminService.getAccountSettings(),
          this.$apiService.ordersService.getStampConfig()
        ])
        .then(results => {
          this.$data.stockList = results[0].data
          this.$data.stockPriceGroupList = results[1].data
          this.$data.orderStampConfig = results[3].data

          var accountSettings = results[2].data

          this.$data.settings.minInvoiceStockId = parseInt(accountSettings[AccountSettingKeys.orderMinInvoiceFeeStockId] || 0)
          if(isNaN(this.$data.settings.minInvoiceStockId))
            this.$data.settings.minInvoiceStockId = null
          this.$data.settings.minInvoiceStock = this.$data.stockList.find(stockItem => stockItem.id == this.$data.settings.minInvoiceStockId)

          this.$data.settings.minPriceStockId = parseInt(accountSettings[AccountSettingKeys.orderMinPriceFeeStockId] || 0)
          if(isNaN(this.$data.settings.minPriceStockId))
            this.$data.settings.minPriceStockId = null
          this.$data.settings.minPriceStock = this.$data.stockList.find(stockItem => stockItem.id == this.$data.settings.minPriceStockId)

          this.$data.settings.templateHandlingStockId = parseInt(accountSettings[AccountSettingKeys.orderTemplateHandlingStockId] || 0)
          if(isNaN(this.$data.settings.templateHandlingStockId))
            this.$data.settings.templateHandlingStockId = null

          this.$data.stockLookupList = this.$data.stockList
            .filter(stock => stock.isActive)
            .map(stock => {
              return {
                value: stock.id,
                name: `${stock.code} | ${stock.name}`,
                code: stock.code,
                type: stock.type,
                isDeliveryFeeType: stock.isDeliveryFeeType,
                stockSalesPrices: stock.stockSalesPrices || []
              }
            })

          // Get delivery stock item
          this.requestDeliveryStockData()         
          .finally(() => {
            if(!options.ignoreLoadingState)
              this.loading = false

            return resolve()
          })
        })
        .catch(err => {
          if(!options.ignoreLoadingState)
            this.loading = false

          return resolve()
        })
      })
    },
    // Get the full item list
    // Used instead of reactivity in v-model for performance reasons
    getItemList() {
      return this.$data.itemList
    },
    
    // Attempt to find the delivery details from an existing item list
    findDeliveryStock() {
      if(!this.settings.deliveryStockId && !this.settings.deliveryStock && this.isDespatchDelivery) {
        var deliveryItem = this.itemList.find(item => item.isDeliveryItem)

        if(deliveryItem) {
          this.settings.deliveryStockId = deliveryItem.stockId
          this.requestDeliveryStockData()
        }
      }
    },
    // Request the delivery stock item data
    // Called when delivery details are updated or on initial load
    requestDeliveryStockData() {
      return new Promise((resolve, reject) => {

        if(this.settings.deliveryStockId) {
          // Note: The line item component refreshes the pricing. We simply set total units
          this.$apiService.inventoryService.getStock(this.settings.deliveryStockId)
          .then(response => {
            this.settings.deliveryStock = response.data
          })
          .finally(() => {
            // Adjust item list
            this.itemListUpdated()

            return resolve()
          })
        } else {
          return resolve()
        }
      })
    },
    newItemSelected(value) {
      this.addItemClicked(value)      
    },
    addItemClicked(stockType, parentIndex = -1, selectInputs = true) {
      var parentItem = null
      var groupStockType = stockType
      if(parentIndex != -1) {
        parentItem = this.itemList[parentIndex]
        groupStockType = parentItem.groupStockType
      }

      // Services can only be added to glass. Go up to next glass item if this isn't glass
      if(stockType == InventoryStockTypes.glassService) {
        if(parentItem && parentItem.stockType != InventoryStockTypes.glass) {
          parentIndex = this.itemList.findIndex(item => item.id == parentItem.parentOrderItemId)
          if(parentIndex == -1)
            return null
          
          parentItem = this.itemList[parentIndex]
          groupStockType = parentItem.groupStockType
        }
      }

      // New order item
      var orderItem = this.createNewOrderItemObject()
      orderItem.groupStockType = groupStockType
      orderItem.stockType = stockType
      orderItem.isParentItem = parentIndex == -1
      orderItem.parentOrderItemId = parentItem ? parentItem.id : null
      orderItem.level = parentItem ? parentItem.level + 1 : 0

      // All new items are external if in external supplier wizard
      if(this.$props.optionSupplierDetailsAvailable)
        orderItem.isExternal = true

      var insertIndex = 0
      if(parentIndex == -1) {

        // Insert above fixed items (like delivery, min invoice item)
        this.itemList.forEach((curItem, index) => {
          if(!(curItem.isFixed && curItem.isParentItem))
            insertIndex = index+1
        })

        this.itemList.splice(insertIndex, 0, orderItem)
      } else {

        // Insert at end of the parent's child item list (but above fixed items like min price)
        insertIndex = parentIndex + 1 + this.countChildItems(parentItem.id, { ignoreFixed: true, ignoreRuleConfigItems: true })
        this.itemList.splice(insertIndex, 0, orderItem)
      }
      if(selectInputs) {
        this.$nextTick(() => {
          let stockEntry = this.$refs[`order-item-${orderItem.id}`][0]
          stockEntry.selectStockEntry()

          this.scrollToComponent(stockEntry)
        })
      }

      // Force cache item update
      this.$data.cachedItemList = _.cloneDeep(this.$data.itemList)

      this.updateItemLoadingStatus()

      return orderItem
    },
    getRootItemIndex(item) {
      if(!item.isParentItem)
        return null

      var index = 0
      for(var listItem of this.itemList) {
        if(listItem.id == item.id)
          return index
        if(listItem.isParentItem)
          index++
      }
      return null
    },
    itemParent(item) {
      return this.itemList.find(parentItem => parentItem.id == item.parentOrderItemId)
    },
    countChildItems(parentItemId, options = {}) {
      // Recursively counts all children's children
      return this.itemList.reduce((total, item) => {
        if(item.parentOrderItemId != parentItemId)
          return total
        if(options.ignoreFixed && item.isFixed)
          return total
        if(options.ignoreRuleConfigItems && item.orderEntryConfigData && item.orderEntryConfigData.isRuleConfigAddedItem)
          return total
        return total + this.countChildItems(item.id) + 1
      }, 0)
    },
    removeItem(id, updateList = true) {

      let itemRemoved = this.itemList.find(item => item.id == id)

      // Remove all children
      var childList = this.itemList.filter(item => item.parentOrderItemId == id)
      childList.forEach(child => {
        this.removeItem(child.id, false)
      })

      this.itemList = this.itemList.filter(item => item.id != id)
      this.$data.cachedItemList = _.cloneDeep(this.$data.itemList)

      // Recalculate pricing for the parent
      if(itemRemoved)
        this.recalculateParentPricing(itemRemoved.parentOrderItemId, { updateList: false, updateCached: false })

      if(updateList)
        this.itemListUpdated()

      this.updateItemLoadingStatus()
    },
    copySupplierToGroup(itemId, supplierContactId) {
      let groupItems = this.getItemGroupListForItem(this.itemList, itemId)

      groupItems.forEach(item => {
        if(item.isExternal) {
          item.supplierContactId = supplierContactId
          let itemIndex = this.itemList.findIndex(listItem => listItem.id == item.id)
          if(itemIndex != -1)
            this.itemUpdated(itemIndex, item)
        }
      })

      // Flat supplier updated
      this.$nextTick(() => {
        groupItems.forEach(item => {
          if(item.isExternal) {
            this.$refs[`order-item-${item.id}`][0].supplierUpdated()
          }
        })
      })
    },
    copyGroupToNewGroup(itemId) {
      let existingItem = this.itemList.find(item => item.id == itemId)
      if(existingItem) {
        let newItem = this.cloneOrderItemGroup(existingItem)

        // Reset quantity / size of the root item
        if(newItem) {
          newItem.quantity = 1
          newItem.glassHeight = 0
          newItem.glassWidth = 0

          let newItemIndex = this.itemList.findIndex(item => item.id == newItem.id)
          this.itemUpdated(newItemIndex, this.itemList[newItemIndex])
          this.itemListUpdated()
          this.updateItemLoadingStatus()

          this.$nextTick(() => {
            try {
              // Select the quantity of the new root item

              this.$refs[`order-item-${newItem.id}`][0].selectQuantityEntry()
            } catch(err) {
              console.error(err)
            }
          })
        }
      }
    },
    itemUpdatedForId(id, itemData) {
      // Search by id in case the item list was editing during updates
      var itemIndex = this.itemList.findIndex(item => item.id == id)
      if(itemIndex != -1)
        this.itemUpdated(itemIndex, itemData)
    },
    itemUpdated(index, itemData) {
      if(!this.editing) {
        this.$set(this.itemList, index, itemData)
        this.itemListUpdated()
        return
      }

      itemData.unitsNotice = ''
      itemData.priceNotice = ''

      if(itemData.isUnitsLocked) {
        if(itemData.parentOrderItemId) {
          this.calculateChildItemQuantity(itemData, this.itemParent(itemData))
        } else {

          if(itemData.stockType == InventoryStockTypes.glass) {
            // Calculate total mm^2 and return as m^2
            var areaMetre = Utils.convertMillimetreSqToMetreSq(this.parseDecimal(itemData.glassHeight) * this.parseDecimal(itemData.glassWidth))
            itemData.units = Utils.roundQuantityValue(areaMetre)
          }
          else {
            itemData.units = 1
          }
        }

        // If below the minimum charge quantity, clamp it to the minimum charge quantity
        // More than likely not used for non-glass items. But here for completeness
        if(itemData.units < itemData.minChargeQuantity) {
          itemData.units = itemData.minChargeQuantity

          // Flag notice
          itemData.unitsNotice = 'Units set to minimum of ' + this.$options.filters.formatDecimal(itemData.minChargeQuantity, 3) + ' per item'
        }
      } else {
        // Just copy down the quantity. Should always match parent
        // The calculateChildItemQuantity() function will stop if units unlocked
        if(itemData.parentOrderItemId) {
          this.calculateChildItemQuantity(itemData, this.itemParent(itemData))
        }
      }

      // Update glass services generated description
      if(itemData.stockType == InventoryStockTypes.glassService) {
        itemData.generatedDescription = ''

        // Generate description for lineal metre edgework flags
        if(itemData.uomType == UOMTypes.length) {
          itemData.generatedDescription = ''
          if(itemData.edgeworkFlagList.length == 4)
            itemData.generatedDescription = 'All Round'
          else if(itemData.edgeworkFlagList.length > 0) {
            itemData.generatedDescription = itemData.edgeworkFlagList.map(flag => Utils.getLookupName(OrderItemEdgeworkTypeLookup, flag)).join(', ')

            // If an order editor item, just use simple short & long descriptions
            // A & Bs cause confusion for complex shapes
            if(itemData.isEditorGeneratedItem) {
              let editorEdgeworkTypeLookup = [
                { name: 'Short', value: OrderItemEdgeworkTypes.shortA },
                { name: 'Short', value: OrderItemEdgeworkTypes.shortB },
                { name: 'Long', value: OrderItemEdgeworkTypes.longA },
                { name: 'Long', value: OrderItemEdgeworkTypes.longB },
                { name: 'All Round', value: OrderItemEdgeworkTypes.allRound },
              ]

              itemData.generatedDescription = itemData.edgeworkFlagList.map(flag => Utils.getLookupName(editorEdgeworkTypeLookup, flag)).join(', ')
            }
          }
        }
      }

      // Update pricing
      this.updateItemPricing(itemData)

      // Update children's units
      this.calculateAllChildrenItemData(itemData)

      // Update pricing again after all children data has been calculated
      // We need to take into account child items for the minimum charge per item pricing as a group
      this.updateItemPricing(itemData)

      this.$set(this.itemList, index, itemData)

      // Update parent pricing to adjust for group charges
      if(itemData.parentOrderItemId) {
        this.recalculateParentPricing(itemData.parentOrderItemId, { updateList: false, updateCached: false })
      }

      // If this is a template, recalculate all children items in the group
      if(itemData.groupStockType == InventoryStockTypes.template) {
        let rootItem = this.findItemRootForItem(itemData)
        if(rootItem)
          this.calculateAllChildrenItemData(rootItem)
      }

      this.itemListUpdated(false)
    },
    itemListUpdated(updateCached = true) {
      if(!this.editing) {
        this.$emit('update', this.itemList)
        return
      }

      // Process all item info
      this.itemList.forEach(item => {
        this.checkItemSettings(item)
        this.checkItemProduction(item)
        this.checkItemPricing(item)
      })

      // Process the stamp configs
      // Adds stamps / no-stamp services based on rules in the config
      this.updateOrderItemStampConfigRules()

      // Process the default edgeworks
      // Adds default edgeworks for non-editor based items
      this.updateOrderItemDefaultEdgeworks()

      // Update the auto-line items
      // Ensure calculation order is: Minimum charges, Minimum invoice, Delivery Item
      this.updateMinChargeLineItems()
      this.updateMinInvoiceLineItem()
      this.updateDeliveryLineItem()

      // Run it again to apply min charge to delivery item if required
      this.updateMinChargeLineItems()

      if(updateCached)
        this.$data.cachedItemList = _.cloneDeep(this.$data.itemList)

      this.$emit('update', this.itemList)

      // Update totals
      var totals = {
        subTotal: this.calculateSubTotalPrice(),
        discountTotal: this.calculateDiscountTotalPrice(),
        tax: this.calculateTaxPrice(),
        total: this.calculateTotalPrice()
      }
      this.$emit('updateTotals', totals)
    },
    itemPricingConfigUpdated(index, itemData) {
      // Force children to download pricing details
      this.itemList.forEach((child, childIndex) => {
        if(child.parentOrderItemId != itemData.id)
          return

        if(this.$refs[`order-item-${child.id}`])
          this.$refs[`order-item-${child.id}`][0].priceGroupUpdated(child.stockPriceGroupId, true, { batchRequest: true })
      })
    },
    itemLoadingUpdate(index, loadingStatus) {
      var item = this.itemList[index]
      this.itemLoadingStatusInfo[item.id] = loadingStatus

      this.updateItemLoadingStatus()
    },
    updateItemLoadingStatus() {
      let orderItemListLoading = false

      // Cleanup map
      let itemIDList = Object.keys(this.itemLoadingStatusInfo)
      itemIDList.forEach(id => {
        if(this.itemList.find(listItem => listItem.id == id) == null) {
          delete this.itemLoadingStatusInfo[id]
        } else {
          orderItemListLoading = this.itemLoadingStatusInfo[id] || orderItemListLoading
        }
      })

      this.$emit('loadingStatusUpdate', orderItemListLoading)      
    },
    isItemExternalAvailable(item) {
      if(item.isDeliveryItem || item.isMinPriceItem || item.isMinInvoiceItem)
        return false
      return true
    },
    allChildrenRequireExternal(item) {
      if(!item.isExternal)
        return false

      // Get all children with external setting available
      let childItemList = this.itemList.filter(childItem => {
        if(childItem.parentOrderItemId == item.id && this.isItemExternalAvailable(childItem))
          return true
        return false
      })
      
      return childItemList.filter(childItem => this.allChildrenRequireExternal(childItem)).length == childItemList.length
    },
    setItemExternal(index, externalValue, applyChildren) {
      if(!this.isItemExternalAvailable(this.itemList[index]))
        return
        
      var item = this.itemList[index]
      this.itemList[index].isExternal = externalValue

      this.itemUpdated(index, this.itemList[index])

      if(applyChildren) {
        this.itemList.forEach((childItem, childIndex) => {
          if(childItem.parentOrderItemId == item.id)
            this.setItemExternal(childIndex, externalValue, true)
        })
      }
    },
    itemToggleExternal(index) {
      var item = this.itemList[index]
      let hasChildren = this.itemList.filter(childItem => childItem.parentOrderItemId == item.id).length > 0

      // Cycle between none -> external -> all external if the item has children
      if(hasChildren) {
        if(item.isExternal) {
          if(this.allChildrenRequireExternal(item)) {
            this.setItemExternal(index, false, true)
          } else {
            // Select all child items to be external
            this.setItemExternal(index, true, true)
          }
        } else {
          // Just set the item value, no children
          this.setItemExternal(index, true, false)
        }
      } else {
        // Simple toggle for single items
        this.setItemExternal(index, !item.isExternal, false)
      }

      this.itemListUpdated()
    },
    itemToggleNCRRepair(index) {
      this.itemList[index].ncrIsRepair = !this.itemList[index].ncrIsRepair

      this.itemUpdated(index, this.itemList[index])
      this.itemListUpdated()
    },

    itemTemplateUpdated(index, itemData) {
      // Clear all children out
      let childList = this.itemList.filter(child => child.parentOrderItemId == itemData.id)
      childList.forEach(child => this.removeItem(child.id, false))
      this.itemListUpdated()

      // Go through template & create the new child items
      if(_.isArray(itemData.templateList)) {
        itemData.templateList.forEach(templateItem => {
          this.addTemplateItem(templateItem, itemData.id)          
        })
      }
    },
    addTemplateItem(templateItem, parentItemId) {
      var parentIndex = this.itemList.findIndex(item => item.id == parentItemId)

      var newItem = this.addItemClicked(templateItem.stockType, parentIndex, false)

      // Glass items in templates are by default locked
      newItem.isGlassSizeLocked = true

      // If fixed data, apply that to the item (ie, stock id)
      if(templateItem.stockId != null)
        newItem.stockId = templateItem.stockId
      // If 1 price group set, set that
      if(_.isArray(templateItem.pricingGroupList) && templateItem.pricingGroupList.length == 1) {
        newItem.stockPriceGroupId = templateItem.pricingGroupList[0]
      }

      this.$nextTick(() => {
        if(templateItem.stockId != null) {
          this.$refs[`order-item-${newItem.id}`][0].setItemStockId(newItem.stockId, { selectInputs: false })
        }        
        if(_.isArray(templateItem.pricingGroupList) && templateItem.pricingGroupList.length == 1) {
          this.$refs[`order-item-${newItem.id}`][0].priceGroupUpdated(newItem.stockPriceGroupId, false)
        }
      })

      // Child items
      if(_.isArray(templateItem.childItems)) {
        templateItem.childItems.forEach(childItem => {
          this.addTemplateItem(childItem, newItem.id)
        })
      }
    },
    // Go through all children of a parent item recursively & update units based on the parent
    calculateAllChildrenItemData(parentItem) {
      this.itemList.forEach((child, childIndex) => {
        if(child.parentOrderItemId != parentItem.id)
          return

        child.priceNotice = ''
        child.unitsNotice = ''

        this.calculateChildItemQuantity(child, parentItem)

        // If below the minimum charge quantity, clamp it to the minimum charge quantity
        // More than likely not used for non-glass items. But here for completeness
        if(child.units < child.minChargeQuantity) {
          child.units = child.minChargeQuantity

          // Flag notice
          child.unitsNotice = 'Units set to minimum of ' + this.$options.filters.formatDecimal(child.minChargeQuantity, 3) + ' per item'
        }

        // Update pricing
        this.updateItemPricing(child)

        this.$set(this.itemList, childIndex, child)

        // Recurse down to children
        // Needed for templates that have multiple nested levels
        this.calculateAllChildrenItemData(child)
      })
    },
    calculateChildItemQuantity(item, parentItem) {
      // Calculate quantity required for a child item of a parent glass item
      // Assumes parent is glass
      // Ignored for order editor items

      item.quantity = parseInt(parentItem.quantity)

      if(!item.isUnitsLocked)
        return
      if(item.isEditorGeneratedItem)
        return

      item.units = 0

      // If the item is part of a template, find the largest glass item in the whole group
      // Use that for unit calculations
      var largestGlassItem = parentItem
      if(item.groupStockType == InventoryStockTypes.template) {
        let groupItems = this.getItemGroupListForItem(this.itemList, item.id)
        groupItems.forEach(groupItem => {
          if(groupItem.stockType == InventoryStockTypes.glass && groupItem.id != item.id) {
            var areaSq = parseInt(groupItem.glassWidth) * parseInt(groupItem.glassHeight)
            if(areaSq > parseInt(largestGlassItem.glassWidth) * parseInt(largestGlassItem.glassHeight)) {
              largestGlassItem = groupItem
            }
          }
        })        
      }

      // Glass / template parent
      // Match units to basic sizing of glass
      if(largestGlassItem.stockType == InventoryStockTypes.glass || largestGlassItem.stockType == InventoryStockTypes.template) {
        switch(item.uomType) {
          case UOMTypes.length:
          {
            let shortLength = Math.min(parseInt(largestGlassItem.glassWidth), parseInt(largestGlassItem.glassHeight))
            let longLength = Math.max(parseInt(largestGlassItem.glassWidth), parseInt(largestGlassItem.glassHeight))

            // Adjust for edgework flags
            var shortCount = item.edgeworkFlagList.filter(edge => edge == OrderItemEdgeworkTypes.shortA || edge == OrderItemEdgeworkTypes.shortB).length
            var longCount = item.edgeworkFlagList.filter(edge => edge == OrderItemEdgeworkTypes.longA || edge == OrderItemEdgeworkTypes.longB).length

            // If this is not a glass service, just calculate all round
            // Assumes regular/rectangle shape
            if(item.stockType != InventoryStockTypes.glassService) {
              shortCount = 2
              longCount = 2
            }

            item.units += Utils.convertMillimetreToMetre(shortLength * shortCount)
            item.units += Utils.convertMillimetreToMetre(longLength * longCount)
          }
          break

          case UOMTypes.metresq:
            if(item.stockType == InventoryStockTypes.glass) {
              if(item.isGlassSizeLocked) {
                item.glassWidth = parseInt(parentItem.glassWidth)
                item.glassHeight = parseInt(parentItem.glassHeight)
              }
              item.units = Utils.convertMillimetreSqToMetreSq(parseInt(item.glassWidth) * parseInt(item.glassHeight))

            } else {
              item.units = Utils.convertMillimetreSqToMetreSq(parseInt(largestGlassItem.glassWidth) * parseInt(largestGlassItem.glassHeight))
            }
            break

          case UOMTypes.each:
            item.units = 1
            break

          case UOMTypes.percentOfLineItem:
            // JB: This method causes precision issues with parent item line price & unit percentages
            // Note: Strictly use parent for percentOfLineItem as largest doesn't apply to price calculations
            item.units = parentItem.itemPrice / 100
            break
        }
      }

      item.units = Utils.roundQuantityValue(item.units)
    },
    updateItemPricing(item) {
      item.priceNotice = ''

      var itemAmount = item.units * Utils.roundCurrency(item.unitPrice)
      if(item.isBlock)
        itemAmount = (item.units * Utils.roundCurrency(item.unitPrice)) * parseInt(item.blockSize)

      item.itemPrice = Utils.roundSubTotalCurrency(itemAmount)
      var lineAmount = itemAmount * item.quantity

      // Discount
      if(item.discountRate > 0)
        item.discountAmount = currency(lineAmount).multiply(parseFloat(item.discountRate)).multiply(0.01).value
      else
        item.discountAmount = 0

      item.linePrice = Utils.roundCurrency(lineAmount - item.discountAmount)
    },
    recalculateParentPricing(parentItemId, options = {}) {
      let parentIndex = this.itemList.findIndex(item => item.id == parentItemId)
      if(parentIndex == -1)
        return

      // Update pricing info for the parent item
      var itemData = this.itemList[parentIndex]
      this.updateItemPricing(itemData)

      this.$set(this.itemList, parentIndex, itemData)

      if(options.updateList)
        this.itemListUpdated(options.updateCached)
    },

    getItemTaxRateId() {
      var taxRate = this.taxRateList.find(taxRate => taxRate.id == this.taxRateId)
      if(taxRate)
        return taxRate.id

      return this.$store.state.account.salesTaxRate.id
    },

    // Check an item's misc settings
    checkItemSettings(item) {
      // Check if a glass item contains a service that matches the 'orderTemplateHandlingStockId' setting
      // If so, sets the isTemplateHandlingRequired flag in the glass item
      if(item.stockType == InventoryStockTypes.glass) {
        if(this.settings.templateHandlingStockId) {
          var childList = this.itemList.filter(child => child.parentOrderItemId == item.id)
          item.isTemplateHandlingRequired = false
          
          if(childList.find(childItem => childItem.stockId == this.settings.templateHandlingStockId) != null)
            item.isTemplateHandlingRequired = true
        }
      }
    },

    // Check an item's production details
    // Flags any warnings
    checkItemProduction(item) {

      item.productionError = ''

      if(item.stockType == InventoryStockTypes.glass) {
        let parentItem = this.itemParent(item)

        // Check glass is available. In stock & size
        if(item.stock.availableGlass && item.stock.isTrackingQuantity) {
          // Stock linked
          if(item.stockPriceGroup && item.stockPriceGroup.isStockLinked) {
            let linkGlassEntry = item.stock.availableGlass.find(glass => glass.id == item.glassLevelId)
            if(linkGlassEntry) {
              if(item.quantity > linkGlassEntry.available && this.isCustomerPricing && !item.isExternal)
                item.productionError += `Not enough glass sheets available. ${linkGlassEntry.available} currently in stock\n`
            } else if(this.isCustomerPricing) {
              item.productionError += `Glass sheet not selected\n`
            }

          } else {
            // Non-stock-linked check
            let suitableGlassList = item.stock.availableGlass.filter(glass => {
              return (item.glassWidth <= glass.width && item.glassHeight <= glass.height) ||
              (item.glassHeight <= glass.width && item.glassWidth <= glass.height)
            })
            let availableCount = suitableGlassList.reduce((curAvailable, glass) => curAvailable + glass.available, 0)
            if(suitableGlassList.length == 0 && this.isCustomerPricing && !item.isExternal)
              item.productionError += `No sheets found large enough to support this item\n`
            else if(availableCount == 0 && this.isCustomerPricing && !item.isExternal)
              item.productionError += `Insufficient stock available on hand\n`
          }
        }

        if(item.glassHeight == 0 || isNaN(item.glassHeight) || item.glassWidth == 0 || isNaN(item.glassWidth))
          item.productionError = `Invalid glass dimensions`

        // Check if parent is a template
        if(parentItem && parentItem.stockType == InventoryStockTypes.template) {
          // If more than 1 glass child item in the template has different glass width/height, flag a production error on the template
          if(item.glassHeight != parentItem.glassHeight || item.glassWidth != parentItem.glassWidth) {
            var childList = this.itemList.filter(child => child.parentOrderItemId == parentItem.id)

            let diffGlassDimensionList = childList.filter(child => {
              // Ignore this item
              if(child.id == item.id)
                return false
              if(child.stockType == InventoryStockTypes.glass && (child.glassHeight != parentItem.glassHeight || child.glassWidth != parentItem.glassWidth))
                return true
              return false
            })

            if(diffGlassDimensionList.length > 0) {
              item.productionError += 'Only 1 glass child item in a template can have modified glass dimensions\n'
            }
          }
        }
      }

      if(item.stockType == InventoryStockTypes.glassService) {
        if(item.units == 0) {
          item.productionError += `Cannot have zero units\n`
        }
      }

      if(item.stockType == InventoryStockTypes.general) {
        if(item.stock.isTrackingQuantity && !item.isExternal) {
          if(item.units * item.quantity > item.stock.quantity && this.isCustomerPricing) {
            item.productionError += `Not enough stock available. ${item.stock.quantity}${item.uomDisplay} currently in stock\n`
          }
        }
      }

      if(item.stockType == InventoryStockTypes.template) {
        if(item.glassHeight == 0 || isNaN(item.glassHeight) || item.glassWidth == 0 || isNaN(item.glassWidth))
          item.productionError += 'Invalid dimensions\n'
      }


      // Update NCR Group flags on the parent
      let groupList = this.getItemGroupListForItem(this.itemList, item.id)
      item.ncrGroupHasRepair = groupList.some(groupItem => groupItem.ncrIsRepair)
    },

    // Check an item's pricing details
    // Flags any warnings
    checkItemPricing(item) {
      item.priceLocalError = ''

      // Ignored for supplier pricing
      if(this.isSupplierPricing)
        return

      if((item.originalUnitPrice > 0 && item.unitPrice < item.originalUnitPrice)) {
        if(item.priceError == null || item.priceError == '')
          item.priceLocalError = 'Unit price is below customer\'s price and will require pricing approval'
      }
      else if(item.discountRate > 0) {
        if(item.priceError == null || item.priceError == '')
          item.priceLocalError = 'Discount applied and will require pricing approval'
      }
    },

    calculateTotalQuantityGlass() {
      if(this.itemList != null) {
        return this.itemList.reduce((curQuantity, item) => {
          if(item.stockType == InventoryStockTypes.glass)
            return curQuantity + parseInt(item.quantity)
          return curQuantity
        }, 0)
      }
      return 0
    },
    calculateTotalUnitsGlass() {
      if(this.itemList != null) {
        return this.itemList.reduce((curUnits, item) => {
          if(item.stockType == InventoryStockTypes.glass) {
            if(item.isBlock) {
              return curUnits + Utils.roundQuantityValue(this.parseDecimal(item.units) * (parseInt(item.blockSize) * parseInt(item.quantity)))
            }
            var areaMetre = Utils.convertMillimetreSqToMetreSq(this.parseDecimal(item.glassHeight) * this.parseDecimal(item.glassWidth))
            var units = Utils.roundQuantityValue(areaMetre)
            return curUnits + Utils.roundQuantityValue(units * parseInt(item.quantity))
          }
          return curUnits
        }, 0)
      }
      return 0
    },

    // Sub total
    // Excluding tax & discounts
    calculateSubTotalPrice() {
      // If an NCR, don't charge anything
      if(this.isNCR)
        return 0

      var totalPrice = 0
      if(this.itemList != null) {
        this.itemList.forEach(item => {
          var linePrice = item.linePrice
          if(item.discountAmount)
            linePrice += item.discountAmount

          // Note: We exclude discountAmount for displaying the sub-total (pre-discount/tax)
          totalPrice += Utils.roundCurrency(linePrice)
        })
      }
      return totalPrice
    },
    calculateDiscountTotalPrice() {
      // If an NCR, don't charge anything
      if(this.isNCR)
        return 0

      if(this.itemList != null) {
        return this.itemList.reduce((cur, item) => {
          if(!item.discountAmount)
            return cur
          return cur + Utils.roundCurrency(item.discountAmount)
        }, 0)
      }
      return 0
    },
    calculateTaxPrice() {
      // If an NCR, don't charge anything
      if(this.isNCR)
        return 0

      var totalTaxPrice = currency(0, { precision: 4 })
      var tempTotal = 0
      if(this.itemList != null) {
        this.itemList.forEach(item => {

          let itemPrice = Utils.roundCurrency(item.linePrice)
          if(item.taxRatePercent)
            totalTaxPrice = totalTaxPrice.add(currency(item.taxRatePercent, { precision: 4 }).multiply(0.01).multiply(itemPrice))
          tempTotal += itemPrice
        })
      }
      return Utils.roundCurrency(totalTaxPrice.value)
    },
    calculateTotalPrice() {
      // If an NCR, don't charge anything
      if(this.isNCR)
        return 0

      return this.calculateSubTotalPrice() - this.calculateDiscountTotalPrice() + this.calculateTaxPrice()
    },
    calculateTotalWeight() {
      if(this.itemList != null) {
        return this.itemList.reduce((curWeight, item) => {
          if(item.stockType == InventoryStockTypes.glass) {
            let totalUnits = 0
            if(item.isBlock) {
              totalUnits = Utils.roundQuantityValue(this.parseDecimal(item.units) * (parseInt(item.blockSize) * parseInt(item.quantity)))
            } else {
              var areaMetre = Utils.convertMillimetreSqToMetreSq(this.parseDecimal(item.glassHeight) * this.parseDecimal(item.glassWidth))
              var units = Utils.roundQuantityValue(areaMetre)
              totalUnits = Utils.roundQuantityValue(units * parseInt(item.quantity))
            }

            return curWeight + Utils.roundNumber(Utils.calculateGlassWeightForArea(totalUnits, item.stock.thickness))
          }
          return curWeight
        }, 0)
      }
      return 0
    },

    // Update the delivery item value via user
    // Typically done from order entry page
    updateDeliveryItemValue(value) {
      var deliveryItem = this.itemList.find(item => item.isDeliveryItem)
      if(deliveryItem) {
        deliveryItem.unitPrice = value
        this.itemListUpdated()
      }
    },

    // Force update on tax rate percentages
    updateItemTaxRates() {
      this.itemList.forEach(item => {
        item.taxRatePercent = this.taxRatePercent
        item.taxRateId = this.getItemTaxRateId()
      })

      this.itemListUpdated()
    },

    // We maintain a special line item for delivery fees using a configured delivery stock item
    // This line item is automatically adjusted to reflect delivery charges
    updateDeliveryLineItem() {
      var deliveryItem = this.itemList.find(item => item.isDeliveryItem)
      var deliveryIndex = this.itemList.findIndex(item => item.isDeliveryItem)
      let isDelivery = this.$props.isDespatchDelivery

      // If delivery & the item doesn't exist, add it
      if(this.settings.deliveryStock) {
        if(isDelivery && deliveryItem == null) {
          deliveryItem = this.createNewOrderItemObject()
          deliveryItem.isDeliveryItem = true
          deliveryItem.isFixed = true
          deliveryItem.groupStockType = this.settings.deliveryStock.type
          deliveryItem.stockType = this.settings.deliveryStock.type
          deliveryItem.description = this.settings.deliveryStock.name
          deliveryItem.stockId = this.settings.deliveryStock.id
          deliveryItem.stock = this.settings.deliveryStock
          deliveryItem.uomType = this.settings.deliveryStock.uomType

          this.itemList.push(deliveryItem)

          // Force new line item to query stock price info
          this.$nextTick(() => {
            let deliveryItemComp = this.$refs[`order-item-${deliveryItem.id}`]
            if(deliveryItemComp)
              deliveryItemComp[0].setItemStockId(deliveryItem.stockId, { selectInputs: false })
          })
        }
      }

      // If pickup and a delivery item exists, remove it
      if(!isDelivery) {
        if(deliveryIndex != -1) {
          // Remove all delivery child items (eg, min delivery charge)
          var childList = this.itemList.filter(item => item.parentOrderItemId == deliveryItem.id)
          childList.reverse().forEach(child => {
            let childIndex = this.itemList.findIndex(item => item.id == child.id)
            if(childIndex != -1)
              this.itemList.splice(childIndex, 1)
          })

          deliveryIndex = this.itemList.findIndex(item => item.isDeliveryItem)
          if(deliveryIndex != -1)
            this.itemList.splice(deliveryIndex, 1)
        }
        return
      }
      // If delivery stock details not downloaded yet, ignore for now
      if(!this.settings.deliveryStock || !deliveryItem)
        return

      deliveryItem.priceNotice = ''

      // If the delivery stock changed, force an update
      if(this.settings.deliveryStock) {
        if(deliveryItem.stockId != this.settings.deliveryStock.id) {
          deliveryItem.groupStockType = this.settings.deliveryStock.type
          deliveryItem.stockType = this.settings.deliveryStock.type
          deliveryItem.description = this.settings.deliveryStock.name
          deliveryItem.stockId = this.settings.deliveryStock.id
          deliveryItem.stock = this.settings.deliveryStock
          deliveryItem.uomType = this.settings.deliveryStock.uomType

          this.$nextTick(() => {
            this.$refs[`order-item-${deliveryItem.id}`][0].setItemStockId(deliveryItem.stockId, { selectInputs: false })
          })
        }
      }

      // Note: The line item component queries the unit price. We just set the total units
      if(deliveryItem.uomType == UOMTypes.percentOfOrder) {
        // Percentage of the order
        // Note: We INCLUDE the minimum invoice line item as part of the levy
        deliveryItem.pricingDescription = 'Delivery Levy'
        
        var totalPrice = this.itemList.reduce((totalPrice, item) => {
          let rootItem = this.findItemRootForItem(item)
          if(item.isDeliveryItem || (rootItem && rootItem.isDeliveryItem))
            return totalPrice
          if(this.isNCR) {            
            if(rootItem && rootItem.isNCRLocked)
              return totalPrice
          }
          return Utils.roundCurrency(totalPrice + item.linePrice)
        }, 0)

        // Total units represents the total order amount
        deliveryItem.units = totalPrice / 100.0
      }
      else {
        // Fixed amount
        deliveryItem.pricingDescription = 'Delivery Fixed Fee'
        deliveryItem.units = 1
      }

      var itemAmount = Utils.roundCurrency(deliveryItem.units * deliveryItem.unitPrice)

      deliveryItem.itemPrice = itemAmount
      var lineAmount = itemAmount * deliveryItem.quantity

      if(deliveryItem.discountRate > 0)
        deliveryItem.discountAmount = currency(lineAmount).multiply(parseFloat(deliveryItem.discountRate)).multiply(0.01).value
      else
        deliveryItem.discountAmount = 0

      deliveryItem.generatedDescription = deliveryItem.pricingDescription
      deliveryItem.linePrice = Utils.roundCurrency(lineAmount - deliveryItem.discountAmount)
    },

    updateMinChargeLineItems() {
      // If no minimum charge stock item setup or downloaded yet, ignore this
      if(!this.settings.minPriceStock) {
        console.log('No minimum price stock')
        return
      }

      // Go through each group & get totals (ignoring special items, like min charge item & cancel fee item)
      // If above min charge & min charge item exists, remove it
      // IF below min charge, add/update the min charge item
      this.itemList.forEach(item => {
        if(item.parentOrderItemId != null || item.isMinInvoiceItem)
          return
        if(item.isCancelFeeItem)
          return

        // Get all group items for the root item
        let groupList = this.getItemGroupListForItem(this.itemList, item.id)
        var minChargeAdjustmentPrice = 0

        var minPriceItem = groupList.find(listItem => listItem.isMinPriceItem)
        if(minPriceItem)
          minPriceItem = this.itemList.find(listItem => listItem.id == minPriceItem.id)

        var groupMinChargePrice = 0
        var groupTotalItemPrice = 0

        groupList.forEach(groupItem => {
          // Ignore min charge items
          if(groupItem.isMinInvoiceItem || groupItem.isMinPriceItem)
            return

          groupMinChargePrice = Math.max(groupMinChargePrice, groupItem.minChargePrice)
          groupTotalItemPrice = Utils.roundCurrency(groupTotalItemPrice + groupItem.itemPrice)
        })

        // Apply flat min charge pricing per item
        if(groupTotalItemPrice < groupMinChargePrice)
          minChargeAdjustmentPrice = groupMinChargePrice - groupTotalItemPrice

        // Adjust the min price item
        if(minChargeAdjustmentPrice > 0 && groupMinChargePrice > 0) {
          // Add / update
          let lastGroupItemIndex = this.itemList.findIndex(listItem => listItem.id == groupList[groupList.length-1].id)
          
          // Add the minimum invoice line item
          if(minPriceItem == null) {
            minPriceItem = this.createNewOrderItemObject()

            minPriceItem.isMinPriceItem = true
            minPriceItem.isParentItem = false
            minPriceItem.isFixed = true
            minPriceItem.groupStockType = item.groupStockType
            minPriceItem.stockType = this.settings.minPriceStock.type
            minPriceItem.description = this.settings.minPriceStock.name
            minPriceItem.stockId = this.settings.minPriceStock.id
            minPriceItem.stock = this.settings.minPriceStock
            minPriceItem.uomType = this.settings.minPriceStock.uomType
            minPriceItem.parentOrderItemId = item.id
            minPriceItem.level = item.level + 1

            this.itemList.splice(lastGroupItemIndex + 1, 0, minPriceItem)
          }

          minPriceItem.pricingDescription = `Min item charge ` + this.$options.filters.formatCurrency(groupMinChargePrice)
          minPriceItem.units = 1
          minPriceItem.quantity = item.quantity
          minPriceItem.unitPrice = minChargeAdjustmentPrice

          var minPriceItemAmount = Utils.roundCurrency(minPriceItem.units * minPriceItem.unitPrice)

          minPriceItem.itemPrice = minPriceItemAmount
          var minPriceLineAmount = minPriceItemAmount * minPriceItem.quantity

          if(minPriceItem.discountRate > 0)
            minPriceItem.discountAmount = currency(minPriceLineAmount).multiply(parseFloat(minPriceItem.discountRate)).multiply(0.01).value
          else
            minPriceItem.discountAmount = 0

          minPriceItem.generatedDescription = minPriceItem.pricingDescription
          minPriceItem.linePrice = Utils.roundCurrency(minPriceLineAmount - minPriceItem.discountAmount)

        } else {
          // Remove the min price item if it exists
          if(minPriceItem != null) {
            let minPriceIndex = this.itemList.findIndex(listItem => listItem.id == minPriceItem.id)
            if(minPriceIndex != -1) {
              this.itemList.splice(minPriceIndex, 1)

              this.updateItemLoadingStatus()
            }
          }
        }
      })
    },

    updateMinInvoiceLineItem() {
      var minInvoiceItem = this.itemList.find(item => item.isMinInvoiceItem)
      let minInvoiceIndex = this.itemList.findIndex(item => item.isMinInvoiceItem)

      // If no minimum invoice stock item setup or downloaded yet, ignore this
      if(!this.settings.minInvoiceStock) {
        console.log('No minimum invoice stock')
        return
      }

      // Don't add minimum invoice charges to NCRs
      if(this.isNCR)
        return

      // Calculate the greatest minimum invoice amount for all order items
      // Also calculate the total price (ex GST) for all the order items
      // Note: We do NOT include delivery item in the minimum invoice total
      // The delivery fee gets added onto after the minimum invoice amount
      var orderMinimumInvoiceAmount = 0
      var orderTotalPrice = 0

      this.itemList.forEach(item => {
        let rootItem = this.findItemRootForItem(item)
        if(item.isMinInvoiceItem || item.isDeliveryItem || (rootItem && rootItem.isDeliveryItem))
          return
        if(this.isNCR) {
          if(rootItem && rootItem.isNCRLocked)
            return
        }
        orderMinimumInvoiceAmount = Utils.roundCurrency(Math.max(orderMinimumInvoiceAmount, item.minChargeInvoice))
        orderTotalPrice = Utils.roundCurrency(orderTotalPrice + item.linePrice)
      }, 0)

      let minInvoiceShortFall = Utils.roundCurrency(orderMinimumInvoiceAmount - orderTotalPrice)

      // If the order total is below the minimum invoice amount, don't add the minimum invoice line item
      // Ensure we remove the min invoice line item if it already exists
      if(minInvoiceShortFall <= 0) {
        if(minInvoiceIndex != -1)
          this.itemList.splice(minInvoiceIndex, 1)

        this.updateItemLoadingStatus()
        return
      }

      if(minInvoiceItem == null) {
        let deliveryIndex = this.itemList.findIndex(item => item.isDeliveryItem)

        // Add the minimum invoice line item
        minInvoiceItem = this.createNewOrderItemObject()

        minInvoiceItem.isMinInvoiceItem = true
        minInvoiceItem.isFixed = true
        minInvoiceItem.groupStockType = this.settings.minInvoiceStock.type
        minInvoiceItem.stockType = this.settings.minInvoiceStock.type
        minInvoiceItem.description = this.settings.minInvoiceStock.name
        minInvoiceItem.stockId = this.settings.minInvoiceStock.id
        minInvoiceItem.stock = this.settings.minInvoiceStock
        minInvoiceItem.uomType = this.settings.minInvoiceStock.uomType

        // Insert it before delivery item if it exists
        if(deliveryIndex != -1)
          this.itemList.splice(deliveryIndex, 0, minInvoiceItem)
        else
          this.itemList.push(minInvoiceItem)

        // Force new line item to query stock price info
        this.$nextTick(() => {
          this.$refs[`order-item-${minInvoiceItem.id}`][0].setItemStockId(minInvoiceItem.stockId, { selectInputs: false })
        })
      }

      if(minInvoiceItem) {
        minInvoiceItem.pricingDescription = `Min Invoice ` + this.$options.filters.formatCurrency(orderMinimumInvoiceAmount)
        minInvoiceItem.units = 1
        minInvoiceItem.quantity = 1
        minInvoiceItem.unitPrice = minInvoiceShortFall

        var itemAmount = Utils.roundCurrency(minInvoiceItem.units * minInvoiceItem.unitPrice)

        // Apply flat min charge pricing per item
        if(itemAmount < minInvoiceItem.minChargePrice) {
          itemAmount = minInvoiceItem.minChargePrice
          minInvoiceItem.priceNotice = 'Pricing set to minimum of ' + this.$options.filters.formatCurrency(minInvoiceItem.minChargePrice) + ' per item'
        }

        minInvoiceItem.itemPrice = itemAmount
        var lineAmount = itemAmount * minInvoiceItem.quantity

        if(minInvoiceItem.discountRate > 0)
          minInvoiceItem.discountAmount = currency(lineAmount).multiply(parseFloat(minInvoiceItem.discountRate)).multiply(0.01).value
        else
          minInvoiceItem.discountAmount = 0

        minInvoiceItem.generatedDescription = minInvoiceItem.pricingDescription
        minInvoiceItem.linePrice = Utils.roundCurrency(lineAmount - minInvoiceItem.discountAmount)
      }
    },


    // Every time the item list is updated, run through & check the stamp order item config rules for each group
    // Add new stock items based on the stamp config rules
    updateOrderItemStampConfigRules() {
      if(!this.orderStampConfig.isAutoApply)
        return

      var listUpdated = false

      // Go through each group
      this.itemList.forEach(item => {
        if(item.parentOrderItemId != null)
          return

        // Ignore editor generated items. Causes duplicates with order editor system which now has stamps
        if(item.isEditorGeneratedItem)
          return

        // Get all group items for the root item
        var groupList = this.getItemGroupListForItem(this.itemList, item.id)
        var currentStockIdList = groupList.map(groupItem => groupItem.stockId)

        var stampRequiredExists = currentStockIdList.indexOf(this.orderStampConfig.stampRequiredStockId) != -1
        var stampNotRequiredExists = currentStockIdList.indexOf(this.orderStampConfig.stampNotRequiredStockId) != -1

        var correctPriceGroup = FilterUtils.itemPassesFilter(this.orderStampConfig.priceGroupFilter, item.stockPriceGroupId)
        if(correctPriceGroup) {
          
          // If neither stock item exists, check for auto apply rules
          if(!stampRequiredExists && !stampNotRequiredExists) {
            
            // Check contact rules if apply stamp or not
            var newServiceStockId = this.orderStampConfig.stampNotRequiredStockId

            if(this.orderStampConfig.defaultStampNotRequiredContactFilter && this.orderStampConfig.defaultStampNotRequiredContactFilter.type == FilterOptionTypes.include) {
              if(!FilterUtils.itemPassesFilter(this.orderStampConfig.defaultStampNotRequiredContactFilter, this.contactId)) {
                newServiceStockId = this.orderStampConfig.stampRequiredStockId
              }
            }

            // Add stock item
            let stockItem = this.stockList.find(stock => stock.id == newServiceStockId)
            if(stockItem) {
              let lastGroupItemIndex = this.itemList.findIndex(listItem => listItem.id == groupList[groupList.length-1].id)

              let autoAddStockItem = this.createNewOrderItemObject()
              autoAddStockItem.isParentItem = false
              autoAddStockItem.groupStockType = item.groupStockType
              autoAddStockItem.stockType = stockItem.type
              autoAddStockItem.description = stockItem.name
              autoAddStockItem.stockId = stockItem.id
              autoAddStockItem.stock = stockItem
              autoAddStockItem.uomType = stockItem.uomType
              autoAddStockItem.parentOrderItemId = item.id
              autoAddStockItem.level = item.level + 1
              autoAddStockItem.orderEntryConfigData.isStampAutoAddedItem = true

              this.itemList.splice(lastGroupItemIndex + 1, 0, autoAddStockItem)
              listUpdated = true

              // Refresh group list
              groupList = this.getItemGroupListForItem(this.itemList, item.id)
              currentStockIdList.push(stockItem.id)

              // Force new line item to query stock price info
              this.$nextTick(() => {
                this.$refs[`order-item-${autoAddStockItem.id}`][0].setItemStockId(autoAddStockItem.stockId, { selectInputs: false })
              })
            }
          } else {

          }
        }
      })

      if(listUpdated)
        this.itemListUpdated()
    },


    updateOrderItemDefaultEdgeworks() {
      var listUpdated = false

      // Go through each group
      this.itemList.forEach(item => {
        if(item.parentOrderItemId != null)
          return

        // Ignore editor generated items
        // Editor handles default edgeworks
        if(item.isEditorGeneratedItem)
          return

        // Ignore if already processed default edgeworks
        // Prevents constantly re-adding if removed & replaced with other edgework
        if(item.orderEntryConfigData && item.orderEntryConfigData.defaultEdgeworkProcessed)
          return

        // Get all group items for the root item
        var groupList = this.getItemGroupListForItem(this.itemList, item.id)
        var currentStockIdList = groupList.map(groupItem => groupItem.stockId)

        if(item.defaultEdgeworkConfig && _.isArray(item.defaultEdgeworkConfig.orderEditorEdgeworkStocks)) {
          // Use first valid stock
          var edgeworkStockInfo = item.defaultEdgeworkConfig.orderEditorEdgeworkStocks[0]

          if(edgeworkStockInfo && edgeworkStockInfo.stock) {

            // Only add if it does not already exists in the group
            if(currentStockIdList.includes(edgeworkStockInfo.stockId))
              return
            
            // Add it & default to all-round
            let stockItem = this.stockList.find(stock => stock.id == edgeworkStockInfo.stockId)
            if(stockItem) {
              let lastGroupItemIndex = this.itemList.findIndex(listItem => listItem.id == groupList[groupList.length-1].id)

              let autoAddEdgeworkItem = this.createNewOrderItemObject()
              autoAddEdgeworkItem.isParentItem = false
              autoAddEdgeworkItem.groupStockType = item.groupStockType
              autoAddEdgeworkItem.stockType = stockItem.type
              autoAddEdgeworkItem.description = stockItem.name
              autoAddEdgeworkItem.stockId = stockItem.id
              autoAddEdgeworkItem.stock = stockItem
              autoAddEdgeworkItem.uomType = stockItem.uomType
              autoAddEdgeworkItem.parentOrderItemId = item.id
              autoAddEdgeworkItem.level = item.level + 1
              autoAddEdgeworkItem.orderEntryConfigData.isDefaultEdgeworkItem = true
              autoAddEdgeworkItem.edgeworkFlagList = [OrderItemEdgeworkTypes.shortA, OrderItemEdgeworkTypes.shortB, OrderItemEdgeworkTypes.longA, OrderItemEdgeworkTypes.longB]

              // Flag as processed default edgework
              if(!item.orderEntryConfigData)
                item.orderEntryConfigData = {}
              item.orderEntryConfigData.defaultEdgeworkProcessed = true

              this.itemList.splice(lastGroupItemIndex + 1, 0, autoAddEdgeworkItem)
              listUpdated = true

              // Refresh group list
              groupList = this.getItemGroupListForItem(this.itemList, item.id)
              currentStockIdList.push(stockItem.id)

              // Force new line item to query stock price info
              this.$nextTick(() => {
                this.$refs[`order-item-${autoAddEdgeworkItem.id}`][0].setItemStockId(autoAddEdgeworkItem.stockId, { selectInputs: false })
              })
            }
          }
        }
      })

      if(listUpdated)
        this.itemListUpdated()
    },

    createNewOrderItemObject() {
      return {
        id: `temp-id-` + uuidv4(),
        loading: false,
        priceLoading: false,
        isDeliveryItem: false,
        isMinInvoiceItem: false,
        isMinPriceItem: false,
        isTemplateHandlingRequired: false,
        isFixed: false,
        isNewItem: true,
        isGlassSizeLocked: false,
        unitPriceOverridden: false,
        isLinePriceLocked: true,
        isUnitsLocked: true,
        isQuantityLocked: false,
        isPriceGroupLocked: false,
        isStockLocked: false,
        isCustomerOwnStock: false,
        isEditorGeneratedItem: false,
        isSplitSheetItem: false,
        
        isNCRLocked: false,
        ncrRequestReason: '',
        ncrRejectionNote: '',
        ncrAccountsChargeNote: '',
        ncrLabelList: [],
        ncrFileList: [],
        ncrIsRepair: false,
        ncrGroupHasRepair: false,

        groupStockType: '',
        stockType: '',
        stockId: null,
        stock: {},
        description: '',
        pricingDescription: '-',
        generatedDescription: '',
        stockPriceGroupId: null,
        stockPriceGroup: {},
        servicePriceRangeId: null,
        servicePriceRange: {},
        glassLevelId: null,
        glassLevel: {},
        glassSizeId: null,
        glassSize: {},
        templateList: [],
        isBlock: false,
        blockSize: 0,
        quantity: 1,
        glassHeight: 0,
        glassWidth: 0,
        minChargePrice: 0,
        minChargeQuantity: 0,
        minChargeInvoice: 0,
        units: 0,
        unitPrice: 0,
        originalUnitPrice: 0,
        itemPrice: 0,
        linePrice: 0,
        uomDisplay: '',
        taxRatePercent: this.taxRatePercent,
        taxRateId: this.getItemTaxRateId(),
        mark: '',
        notes: '',
        orderEditorNotes: '',
        discountRate: 0,
        discountAmount: 0,
        requiresCustomerApproval: false,
        isExternal: false,
        edgeworkFlagList: [],
        svgDisplayData: '',
        svgLabelData: '',
        svgLabelCornerData: '',
        showingGlass: false,
        isParentItem: true,
        parentOrderItemId: null,
        level: 0,
        priceNotice: '',
        unitsNotice: '',
        priceError: '',
        priceLocalError: '',
        productionError: '',
        productionConfigError: '',
        productionStockError: '',
        productionEditorError: '',
        despatchedQuantity: 0,
        invoicedQuantity: 0,

        orderEntryConfigData: {}
      }
    },

    overrideUpdated(value) {
      this.$emit('overrideUpdated', value)
    },
    itemSelectGlass(index) {
      if(this.isCustomerPricing) {
        // Display the glass level dialog to select a glass level from inventory
        this.displayModal(OrderSelectGlassLevelDialog, { 
          stockId: parseInt(this.itemList[index].stockId)
        })
        .then(glassLevelInfo => {
          if(glassLevelInfo) {
            this.itemList[index].glassLevelId = glassLevelInfo.id
            this.itemList[index].glassLevel = glassLevelInfo
            this.itemList[index].glassWidth = glassLevelInfo.width
            this.itemList[index].glassHeight = glassLevelInfo.height

            this.itemUpdated(index, this.itemList[index])
          }
        })
      }

      if(this.isSupplierPricing) {
        // Display the glass size dialog to select a glass size provided by a supplier
        this.displayModal(OrderSelectGlassSizeDialog, { 
          stockId: parseInt(this.itemList[index].stockId),
          glassSizeId: parseInt(this.itemList[index].glassSizeId || 0),
          orderedGlassWidth: parseInt(this.itemList[index].originalGlassWidth),
          orderedGlassHeight: parseInt(this.itemList[index].originalGlassHeight),
          isBlock: this.itemList[index].isBlock,
          blockSize: parseInt(this.itemList[index].blockSize || 0)
        })
        .then(glassSizeInfo => {
          if(glassSizeInfo) {
            this.itemList[index].glassSizeId = glassSizeInfo.id
            this.itemList[index].glassSize = glassSizeInfo
            this.itemList[index].glassWidth = glassSizeInfo.width
            this.itemList[index].glassHeight = glassSizeInfo.height
            this.itemList[index].isBlock = glassSizeInfo.isBlock
            this.itemList[index].blockSize = glassSizeInfo.blockSize

            this.itemUpdated(index, this.itemList[index])
          }
        })
      }
    },
    itemSelectNCR(index) {
      let originalQuantity = parseInt(this.itemList[index].ncrOriginalQuantity)

      this.displayModal(OrderItemNCRDialog, { 
        originalQuantity: originalQuantity,
        submitTitle: 'Add',
        isNCRSetup: true,
        isNewOrder: this.isNewOrder,
        submitPromise: (data) => {
          return new Promise((resolve, reject) => {
            // Split into 2 order items if some remake & some repaired
            if(data.quantity > 0 && data.repairQuantity > 0) {
              this.itemList[index].ncrLabelList = data.ncrLabelList
              this.itemList[index].isNCRLocked = false
              this.itemList[index].ncrRequestReason = data.ncrRequestReason
              this.itemList[index].quantity = data.quantity
              this.itemList[index].ncrIsRepair = false
              this.itemList[index].ncrGroupHasRepair = false
              this.itemUpdated(index, this.itemList[index])

              // Split up item into 2 groups. 1 for remake, 1 for repairs
              let newItem = this.cloneOrderItemGroup(this.itemList[index])
              if(newItem) {
                newItem.ncrIsRepair = true
                newItem.ncrGroupHasRepair = true
                newItem.quantity = data.repairQuantity

                let newItemIndex = this.itemList.findIndex(item => item.id == newItem.id)
                this.itemUpdated(newItemIndex, this.itemList[newItemIndex])
                this.itemListUpdated(true)
              }

            } else {
              this.itemList[index].ncrLabelList = data.ncrLabelList
              this.itemList[index].isNCRLocked = false
              this.itemList[index].ncrRequestReason = data.ncrRequestReason
              this.itemList[index].quantity = data.quantity
              this.itemList[index].ncrIsRepair = false
              this.itemList[index].ncrGroupHasRepair = false

              if(data.repairQuantity > 0) {
                this.itemList[index].quantity = data.repairQuantity
                this.itemList[index].ncrIsRepair = true
                this.itemList[index].ncrGroupHasRepair = true
              }

              this.itemUpdated(index, this.itemList[index])
            }

            return resolve()
          })          
        }
      })
      .then((success) => {
      })
    },


    // Toolbar Tools
    displayApplyAllService() {
      this.$refs.applyAllServicePopover.displayFromElement(this.$refs.applyAllServiceButton.$el)
    },
    addAllService(settings) {
      // Go through and copy the item to other parent items as a child
      // Currently only handles child Glass Services / General items
      settings.itemList.forEach(destItem => {
        var parentIndex = this.itemList.findIndex(listItem => listItem.id == destItem.id)
        if(parentIndex != -1) {
          var newItem = this.addItemClicked(InventoryStockTypes.glassService, parentIndex, false)

          // Set details
          newItem.stockId = settings.stockId
          newItem.edgeworkFlagList = _.clone(settings.edgeworkFlagList)

          this.$nextTick(() => {
            if(newItem.stockId != null) {
              this.$refs[`order-item-${newItem.id}`][0].setItemStockId(newItem.stockId, { selectInputs: false })
            }
          })

          let itemIndex = this.itemList.findIndex(item => item.id == newItem.id)
          if(itemIndex != -1)
            this.itemUpdated(itemIndex, newItem)
        }
      })
    },
    displayApplyAllDiscount() {
      this.$refs.applyAllDiscountPopover.displayFromElement(this.$refs.applyAllDiscountButton.$el)
    },
    applyAllDiscount(settings) {

      settings.itemList.forEach(destItem => {
        var itemIndex = this.itemList.findIndex(listItem => listItem.id == destItem.id)
        if(itemIndex != -1) {
          var item = this.itemList[itemIndex]

          item.discountRate = settings.discountRate || 0
          this.itemUpdated(itemIndex, item)
        }
      })
    },
    displayApplyAllCustomerApproval() {
      this.$refs.applyAllCustomerApprovalPopover.displayFromElement(this.$refs.applyAllCustomerApprovalButton.$el)
    },
    applyAllCustomerApproval(settings) {
      settings.itemList.forEach(destItem => {
        var itemIndex = this.itemList.findIndex(listItem => listItem.id == destItem.id)
        if(itemIndex != -1) {
          var item = this.itemList[itemIndex]

          item.requiresCustomerApproval = settings.requiresCustomerApproval
          this.itemUpdated(itemIndex, item)
        }
      })
    },
    displayApplyAllSupplier() {
      this.$refs.applyAllSupplierPopover.displayFromElement(this.$refs.applyAllSupplierButton.$el)
    },
    applyAllSupplier(settings) {
      settings.itemList.forEach(destItem => {
        var itemIndex = this.itemList.findIndex(listItem => listItem.id == destItem.id)
        if(itemIndex != -1) {
          var item = this.itemList[itemIndex]

          item.supplierContactId = settings.supplierContactId
          this.itemUpdated(itemIndex, item)

          this.$nextTick(() => {
            this.$refs[`order-item-${item.id}`][0].supplierUpdated()
          })
        }
      })
    },
    displayOrderEditor() {
      this.$emit('startOrderEditor')
    },

    copyBuildOrderItemList(itemList, parentOrderItemId = null, outputList = [], level = 0) {
      let parentItem = itemList.find(orderItem => orderItem.id == parentOrderItemId)

      // Flatten & order so children are after parents
      itemList.filter(orderItem => orderItem.parentOrderItemId == parentOrderItemId).forEach(orderItem => {
        var item = _.cloneDeep(orderItem)
        let rootItem = this.findRootOrderItemForParentId(itemList, item.parentOrderItemId)

        item.loading = false
        item.notes = item.notes || ''
        item.orderEditorNotes = item.orderEditorNotes || ''
        item.mark = item.mark || ''
        item.uomType = orderItem.stock.uomType
        item.uomDisplay = Utils.getLookupName(UOMTypeUnitsLookup, orderItem.stock.uomType)
        item.isParentItem = item.parentOrderItemId == null
        item.level = level
        item.groupStockType = rootItem ? rootItem.stockType : item.stockType
        item.showingGlass = false
        item.unitPriceOverridden = !!item.unitPriceOverridden
        item.isUnitsLocked = !item.unitsOverridden
        item.isGlassSizeLocked = parentItem ? true : false
        item.isLinePriceLocked = true
        item.isQuantityLocked = false
        item.isPriceGroupLocked = false
        item.isStockLocked = false
        item.despatchedQuantity = 0
        item.invoicedQuantity = 0
        item.isInvoiced = false
        item.isCancelled = false
        item.isDespatched = false
        item.isCancelFeeItem = !!item.isCancelFeeItem

        // NCR details
        item.isNCRLocked = !!item.isNCRLocked
        item.ncrRequestReason = item.ncrRequestReason || ''
        item.ncrRejectionNote = item.ncrRejectionNote || ''
        item.ncrAccountsChargeNote = item.ncrAccountsChargeNote || ''
        item.ncrLabelList = item.ncrLabelList || []
        item.ncrUploadFileList = item.ncrUploadFileList || []
        item.ncrIsRepair = !!item.ncrIsRepair
        item.ncrGroupHasRepair = !!item.ncrGroupHasRepair

        outputList.push(item)

        this.copyBuildOrderItemList(itemList, item.id, outputList, level + 1)
      })

      return outputList      
    },

    // Copy existing order items from an existing order / from glass editor
    // existingOrderItems is the flattened structure from the server
    // Ignores the delivery/min-invoice items
    copyOrderItemList(existingOrderItems, parentOrderItemId = null, parentIndex = -1, options = {}) {

      this.itemList = this.copyBuildOrderItemList(existingOrderItems.filter(item => {
        if(item.isDeliveryItem || item.isMinInvoiceItem)
          return false

        // Only copy over other special items like cancel fees if building the list during the order editor
        if(item.isCancelFeeItem && !options.isEditorItemCopy)
          return false

        return true
      }))

      // For NCRs we unlock the fields so it ensures it copies over the same pricing as the original order & other fields cannot be updated normally
      if(options.isNCR) {
        this.itemList.forEach(item => {
          // Setup the NCR defaults for all items
          item.isNCRLocked = true
          item.ncrRequestReason = item.ncrRequestReason || ''
          item.ncrRejectionNote = item.ncrRejectionNote || ''
          item.ncrAccountsChargeNote = item.ncrAccountsChargeNote || ''
          item.ncrLabelList = []
          item.ncrUploadFileList = []
          item.ncrIsRepair = !!item.ncrIsRepair
          item.ncrGroupHasRepair = !!item.ncrGroupHasRepair
          item.originalOrderItemId = item.id || null
          item.ncrOriginalQuantity = parseInt(item.quantity || 0)

          // Adjust locks
          item.unitPriceOverridden = true
          item.isQuantityLocked = true
        })
      }

      this.itemListUpdated()

      // Query server info for stock / pricing details
      this.$nextTick(() => {
        try {
          this.itemList.forEach(item => {
            this.$refs[`order-item-${item.id}`][0].setItemStockId(item.stockId, {
              selectInputs: false,
              reset: false,
              updateList: false,
              ignoreTemplateUpdate: true,
              batchRequest: true
            })
          })
        } catch(err) {
        }
      })
    },


    // Clone an entire order item group & add it below the existing group
    // Adds into the itemList list
    // Calculates the item group based on any item in the group
    // Returns the root item of the new group
    cloneOrderItemGroup(sourceGroupItem) {
      let sourceRootItem = this.findItemRootForItem(sourceGroupItem)
      let sourceGroupList = this.getItemGroupListForItem(this.itemList, sourceRootItem.id)

      let rootItemIndex = this.itemList.findIndex(item => item.id == sourceRootItem.id)
      let destItemIndex = rootItemIndex + sourceGroupList.length

      if(sourceGroupList.length == 0)
        return null

      let destItems = []
      sourceGroupList.forEach(sourceItem => {
        var newOrderItem = _.cloneDeep(sourceItem)
        newOrderItem.id = `temp-id-` + uuidv4()
        newOrderItem.isNewItem = true
        newOrderItem.parentOrderItemId = null
        destItems.push(newOrderItem)
      })

      // Re-link items
      sourceGroupList.forEach((sourceItem, index) => {
        let parentIndex = sourceGroupList.findIndex(item => item.id == sourceItem.parentOrderItemId)
        
        if(parentIndex != -1)
          destItems[index].parentOrderItemId = destItems[parentIndex].id
      })

      destItems.forEach(destOrderItem => {
        this.itemList.splice(destItemIndex++, 0, destOrderItem)
      })

      return this.findRootOrderItemForParentId(this.itemList, destItems[0].id)
    }
  }
}
</script>